import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, Share, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrap } from '../../lib/api-envelope';
import { isInternalRole } from '../../lib/roles';
import {
  appleDeviceName, deviceKind, markPushPromptSeen, notificationPermission, pushPromptSeen, pushSupport, turnOnPush,
} from '../../lib/push';

/**
 * The ONE-TIME "get alerts on this phone / tablet / computer" card. Shown at most once per person
 * per device, then never again: dismissing it or acting on it records that
 * (lib/push.ts), and Settings → Notifications is the way back. It never asks
 * on its own — the browser's permission prompt only ever follows a tap.
 *
 * Two forms:
 *   - this browser can take push and the person has not been asked:
 *       "Get job alerts on this phone"  [Turn on] [Not now]
 *     The device is named for what it is (deviceKind). An office user on a
 *     computer reads "Get alerts on this computer": at a desk the alerts are
 *     the daily alert and approvals as much as jobs.
 *   - an iPhone in a Safari tab, which cannot take push at all:
 *       the three Add-to-Home-Screen steps, once.
 * Nothing when the server has no push keys (asking would promise alerts that
 * cannot arrive), when permission is already granted or refused, inside the
 * native shell, or for a portal user.
 */
export default function PushPrompt() {
  const { isAuthenticated, user, isFieldWorker } = useAuth();
  const userId: string | undefined = user?.id ? String(user.id) : undefined;
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const support = pushSupport();
  const eligible = Boolean(isAuthenticated && userId && isInternalRole(user?.role))
    && support !== 'unsupported'
    && !pushPromptSeen(userId ?? '')
    && (support === 'ios_needs_home' || notificationPermission() === 'default');

  const { data: enabled } = useQuery({
    queryKey: ['push', 'config'],
    queryFn: async () => unwrap<{ webPush: { enabled: boolean } }>((await api.push.config()).data)?.webPush?.enabled === true,
    enabled: eligible,
    staleTime: 60 * 60 * 1000,
  });

  if (!eligible || hidden || enabled !== true || !userId) return null;

  const kind = deviceKind();
  const title = !isFieldWorker && kind === 'computer'
    ? 'Get alerts on this computer'
    : `Get job alerts on this ${kind}`;

  const close = () => {
    markPushPromptSeen(userId);
    setHidden(true);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      const result = await turnOnPush();
      if (result === 'granted') toast.success(`Alerts are on for this ${kind}`);
      else if (result === 'denied') toast.error('Alerts are blocked. You can allow them in your browser settings.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not turn on alerts');
    } finally {
      setBusy(false);
      close();
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="push-prompt-title"
      className="fixed inset-x-0 z-40 mx-4 md:mx-auto md:max-w-md bottom-[calc(var(--bottom-chrome,0px)+12px)] bg-fieldiq-card border border-fieldiq-border rounded-xl shadow-lg p-4 text-white"
    >
      <div className="flex items-start gap-3">
        <Bell className="w-5 h-5 text-fieldiq-accent shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          <h2 id="push-prompt-title" className="text-sm font-semibold">{title}</h2>
          {support === 'ios_needs_home' ? (
            <>
              <p className="text-xs text-gray-400 mt-1">On {appleDeviceName()}, alerts work from the FieldIQ app on your Home Screen:</p>
              <ol className="text-xs text-gray-300 mt-2 space-y-1 list-decimal list-inside">
                <li>Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Share" /> Share in Safari</li>
                <li>Choose <span className="font-medium">Add to Home Screen</span></li>
                <li>Open FieldIQ from the new icon, sign in, and turn on alerts</li>
              </ol>
              <div className="mt-3 flex justify-end">
                <button onClick={close} className="bg-fieldiq-card border border-fieldiq-border px-3 py-1.5 rounded-lg text-sm">Got it</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400 mt-1">New jobs, job chat and reminders, even when FieldIQ is closed. You choose which in Settings → Notifications.</p>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={close} disabled={busy} className="bg-fieldiq-card border border-fieldiq-border px-3 py-1.5 rounded-lg text-sm disabled:opacity-50">Not now</button>
                <button onClick={turnOn} disabled={busy} className="bg-fieldiq-accent text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
                  {busy ? 'Turning on…' : 'Turn on'}
                </button>
              </div>
            </>
          )}
        </div>
        <button onClick={close} aria-label="Dismiss" className="text-gray-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
